// dependencies.
import { useState } from 'react'
import styled from 'styled-components'
// components.
import { Body } from '../../../Typography'
import { Icon } from '../../../Icon'
// utils.
import { theme } from '../../../../styleguide'
// constants.
import { minCellsWidth, COLUMN_LAYOUT_START, COLUMN_LAYOUT_END } from './Item'
import { AUDITORIES_TABLE_HEADER_HEIGHT } from '../../../../constants'

// helpers
const baseUnit = theme.fontSize.x16

// partials.
const Wrapper = styled.li({
  backgroundColor: theme.colors.white,
  borderBottom: `2px solid ${theme.opacity.ash80}`,
  display: 'flex',
  flexDirection: 'row',
  height: AUDITORIES_TABLE_HEADER_HEIGHT,
  maxWidth: '100%',
  position: 'absolute',
  right: 0,
  top: 0,
  width: '100%',
  zIndex: 8,
})

const Cell = styled.div({
  alignItems: 'center',
  backgroundColor: (props) => (props.isDynamic ? theme.colors.ash : 'none'),
  borderRight: (props) =>
    `1px solid ${props.isDynamic ? theme.opacity.black20 : theme.opacity.ash80}`,
  cursor: (props) => (props.isSortable ? 'pointer' : 'default'),
  display: 'flex',
  flexDirection: 'row',
  gap: baseUnit / 2,
  height: '100%',
  justifyContent: 'center',
  padding: 0,
  width: (props) => `${props.width}%`,
  zIndex: 0,
  ...(props) =>
    props.isSortable && {
      '&:focus': {
        backgroundColor: theme.colors.smoke,
        filter: `drop-shadow(${theme.shadows.focus})`,
        outline: 'none',
        zIndex: 1,
      },
    },
})

const StyledSortLabel = styled(Body)({
  userSelect: 'none',
  textAlign: 'center',
})

const StyledRotateLabel = styled(Body)({
  lineHeight: 0.8,
  minWidth: AUDITORIES_TABLE_HEADER_HEIGHT - 16,
  userSelect: 'none',
  transform: `translate(50%, ${AUDITORIES_TABLE_HEADER_HEIGHT / 2 - 8}px) rotate(-90deg)`,
  transformOrigin: 'center left',
})

// main component.
const AuditoriesDesktopSort = ({ dynamicColumns, style, onSort }) => {
  const [sortingBy, setSortingBy] = useState({ label: '', key: 0, direction: null })

  const sortingDirectionIcon = [null, 'caret-down', 'caret-up']

  const handleSort = (sortLabel, sortDbKey) => {
    const index =
      sortingBy.label === sortLabel && sortingBy.direction === 'caret-down'
        ? 2
        : sortingBy.direction === 'caret-up'
          ? 0
          : 1
    const direction = sortingDirectionIcon[index]
    const sortingObj = { label: sortLabel, key: sortDbKey, direction }
    setSortingBy(sortingObj)
    onSort(sortingObj)
  }

  const cellWidth = 100 / (minCellsWidth + dynamicColumns.length)

  // return content.
  return (
    <Wrapper style={style}>
      {COLUMN_LAYOUT_START.map(({ label, dbKey, columns, isDynamic }) => {
        const maybeSortable = !!dbKey
        const isSorting = maybeSortable && sortingBy.direction && label === sortingBy.label

        return (
          <Cell
            key={label}
            isSortable={maybeSortable}
            width={cellWidth * columns}
            tabIndex={maybeSortable ? 0 : undefined}
            onClick={() => maybeSortable && handleSort(label, dbKey)}
            isDynamic={isDynamic}
          >
            {isDynamic ? (
              <StyledRotateLabel type="caption">
                <small>{dbKey === 'number' ? '#' : label}</small>
              </StyledRotateLabel>
            ) : (
              <>
                <StyledSortLabel type="caption" color={isSorting ? 'black' : 'grey'}>
                  <small>{dbKey === 'number' ? '#' : label}</small>
                </StyledSortLabel>
                {isSorting && <Icon name={sortingBy.direction} size={12} color="black" />}
              </>
            )}
          </Cell>
        )
      })}
      {dynamicColumns.map(({ label }) => {
        return (
          <Cell key={label} width={cellWidth} isDynamic>
            <StyledRotateLabel type="caption">
              <small>{label}</small>
            </StyledRotateLabel>
          </Cell>
        )
      })}
      {COLUMN_LAYOUT_END.map(({ label, dbKey, columns }) => {
        const maybeSortable = !!dbKey
        const isSorting = maybeSortable && sortingBy.direction && label === sortingBy.label

        return (
          <Cell
            key={label}
            isSortable={maybeSortable}
            width={cellWidth * columns}
            tabIndex={maybeSortable ? 0 : undefined}
            onClick={() => maybeSortable && handleSort(label, dbKey)}
          >
            <StyledSortLabel type="caption" color={isSorting ? 'black' : 'grey'}>
              <small>{label}</small>
            </StyledSortLabel>
            {isSorting && <Icon name={sortingBy.direction} size={12} color="black" />}
          </Cell>
        )
      })}
    </Wrapper>
  )
}

export default AuditoriesDesktopSort
